import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  useOrderTargets,
  useTargetBreakdownsByTargetIds,
} from '../../../services/modules/target/target.queries';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import { getOrderTargetsTabPath } from '../../orders/utils/orderTargetsTab.utils';
import { useFeatureVisibility } from '../../permissions/contexts/FeatureVisibilityContext';
import { F } from '../../permissions/types/features.keys';
import { OrderTargetListRow } from './orderTargets.types';
import { toOrderTargetBreakdownRows } from './orderTargets.utils';

const useOrderTargetList = (orderId: string) => {
  const history = useHistory();
  const handleCloseOrderBook = useOrderBookStore((state) => state.handleCloseOrderBook);
  const { isVisible } = useFeatureVisibility();
  const canOpenTarget = isVisible(F.targets.viewOrderTargets);
  const [expandedTargetId, setExpandedTargetId] = useState<string | null>(null);
  const { data, isFetching, isError, refetch } = useOrderTargets(orderId);
  const orderTargets = data ?? [];
  const breakdowns = useTargetBreakdownsByTargetIds(
    orderTargets.map((target) => target.id),
    new Set(expandedTargetId ? [expandedTargetId] : []),
  );

  const targets: OrderTargetListRow[] = orderTargets.map((target, index) => {
    const breakdown = breakdowns[index];

    return {
      id: target.id,
      code: target.code,
      status: target.status,
      unit: target.unit,
      attainedValue: target.attainment.attained,
      plannedValue: target.attainment.planned,
      attainment: {
        conversion: target.attainment.conversion,
        attainedByStatus: target.attainment.attainedByStatus,
        calculatedAt: target.attainment.calculatedAt,
      },
      breakdown: {
        items: toOrderTargetBreakdownRows({
          items: breakdown?.items ?? [],
          calculatedAt: target.attainment.calculatedAt,
        }),
        isLoading: breakdown?.isLoading ?? false,
        isError: !!breakdown?.isError && !breakdown?.items,
        reload: () => {
          breakdown?.refetch();
        },
      },
    };
  });

  const reload = () => {
    refetch();
  };

  const toggleTarget = (targetId: string) => {
    setExpandedTargetId((current) => (current === targetId ? null : targetId));
  };

  const openTarget = (targetId: string) => {
    history.push(getOrderTargetsTabPath({ orderId, targetId }));
    handleCloseOrderBook();
  };

  return {
    targets,
    isFetching,
    isError,
    canOpenTarget,
    expandedTargetId,
    reload,
    toggleTarget,
    openTarget,
  };
};

export default useOrderTargetList;
